import React from 'react'
import {
    addHyperlinkToFirstColumn,
    Cell,
    TableData,
    filterRows,
    applyConditionalFormatting,
} from 'utils/data'
import { translateHeadings } from 'locales/utils'
import { isGroup, isDistrict, isStore } from 'utils/location'
import { GeneralizedComponentPayloadType } from 'components/generalized-component/generalized-component'
import Icon, { ShipBoxIcon } from '@design-systems/react-icons'
import priorityData from './priority.json'

export interface Location {
    location_id?: number
    location_description?: string
    carry_over_units?: number
    open_late_units?: number
    open_late_units_color?: string
    open_by_carrier_pickup_to_be_picked?: number
    open_by_carrier_pickup_to_be_packed?: number
    open_by_store_close_to_be_picked?: number
    open_by_store_close_to_be_packed?: number
    sorted_units?: number
    forecast_units?: number
    inf_percent?: string
    inf_percent_color?: string
    open_units_required_ship_today?: number
}

export interface ShipFromStorePortrait {
    sfs_time_utc: string
    current_location: Location | null
    parent_locations: Location[] | null
    children_locations: Location[] | null
    grand_children_locations: Location[] | null
}

export interface PortraitData {
    ship_from_store: {
        ship_from_store_landscape: ShipFromStorePortrait
        ship_from_store_portrait: ShipFromStorePortrait
    }
}

export function formatWithCommas(num: number | string): string {
    return new Intl.NumberFormat('en-US').format(Number(num))
}

export function isPriorityStore(locationDescription: string): boolean {
    return priorityData.priorityStores.some(
        (store) => store.store === locationDescription,
    )
}

export const makeSortFn =
    (direction: 'asc' | 'dsc' | 'desc' | 'none' | undefined, index = 1) =>
    (a: Cell[], b: Cell[]) => {
        if (!direction || direction === 'none') return 0
        const isDesc = direction === 'dsc' || direction === 'desc'
        if (index === 0) {
            const aVal = a[index]?.raw || a[index]?.value || ''
            const bVal = b[index]?.raw || b[index]?.value || ''
            return isDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal)
        }

        // For numeric columns, use raw values if available, otherwise parse formatted values
        const getValue = (c: Cell) => {
            if (c.raw !== undefined) {
                return parseFloat(c.raw) || 0
            }
            return parseFloat(c.value.replace(/[,%$]/g, '') || '0')
        }
        const aNum = getValue(a[index])
        const bNum = getValue(b[index])

        return isDesc ? bNum - aNum : aNum - bNum
    }

export function pinAndSortRows(
    rows: Cell[][],
    sortFn: (a: Cell[], b: Cell[]) => number,
): Cell[][] {
    const pinned: Cell[][] = []
    const sortable: Cell[][] = []

    rows.forEach((row) => {
        const source = row[0]?.childOf
        if (source === 'current' || source === 'parent') pinned.push(row)
        else sortable.push(row)
    })

    sortable.sort(sortFn)
    return [...pinned, ...sortable]
}

export function createTranslatedTable(
    t: (key: string) => string,
    headings: Record<string, string>,
): TableData {
    const headingKeys = [
        headings.location,
        headings.openLateUnits,
        headings.carryOverUnits,
        headings.openUnitsCarrier,
        headings.openUnitsPack,
        headings.unitsPacked,
        headings.unitPlan,
        headings.infPercent,
    ]
    const tableData: TableData = {
        headings: headingKeys.map((value) => ({ value })),
        rows: [],
    }
    return translateHeadings(headingKeys, t, tableData)
}

export function gatherLocationRows(
    portrait: ShipFromStorePortrait,
    makeRow: (
        loc: Location,
        src: 'current' | 'parent' | 'child' | 'grandchild',
    ) => Cell[],
): Cell[][] {
    const rows: Cell[][] = []
    portrait.parent_locations?.forEach((loc) =>
        rows.push(makeRow(loc, 'parent')),
    )
    if (portrait.current_location) {
        rows.push(makeRow(portrait.current_location, 'current'))
    }
    portrait.children_locations?.forEach((loc) =>
        rows.push(makeRow(loc, 'child')),
    )
    portrait.grand_children_locations?.forEach((loc) =>
        rows.push(makeRow(loc, 'grandchild')),
    )
    return rows
}

export function buildPayloadFromTable(
    table: TableData,
    currentLocation: string,
    t: (key: string) => string,
    toggles: Record<string, string>,
): GeneralizedComponentPayloadType {
    if (isGroup(currentLocation)) {
        return {
            [t(toggles.showDistricts)]: [
                {
                    title: '',
                    subtitle: '',
                    data: {
                        group: filterRows(
                            table,
                            (row) => !isStore(row[0].value),
                        ),
                    },
                },
            ],
            [t(toggles.showStores)]: [
                {
                    title: '',
                    subtitle: '',
                    data: {
                        group: filterRows(
                            table,
                            (row) => !isDistrict(row[0].value),
                        ),
                    },
                },
            ],
        }
    }
    return {
        // placeholder key for default view
        'view in %': [
            {
                title: '',
                subtitle: '',
                data: { nonGroup: table },
            },
        ],
    }
}

/**
 * Full payload builder, including translation, row generation, pin/sort, and links
 */
export function buildDefaultGenCompPayload(
    data: PortraitData[],
    deps: {
        headings: Record<string, string>
        currentLocation: string
        t: (key: string) => string
        toggles: Record<string, string>
    },
): GeneralizedComponentPayloadType {
    const { headings, currentLocation, t, toggles } = deps
    const landscapeData = data[0]?.ship_from_store?.ship_from_store_landscape
    const portraitData = data[0]?.ship_from_store?.ship_from_store_portrait

    const table = createTranslatedTable(t, headings)

    // Helper function to get portrait value for the 4th column
    const getPortraitValue = (
        locationId: number,
        src: 'current' | 'parent' | 'child' | 'grandchild',
    ): number => {
        if (!portraitData) return 0

        if (src === 'current') {
            return (
                portraitData.current_location?.open_units_required_ship_today ??
                0
            )
        } else if (src === 'parent') {
            const portraitLoc = portraitData.parent_locations?.find(
                (loc) => loc.location_id === locationId,
            )
            return portraitLoc?.open_units_required_ship_today ?? 0
        } else if (src === 'child') {
            const portraitLoc = portraitData.children_locations?.find(
                (loc) => loc.location_id === locationId,
            )
            return portraitLoc?.open_units_required_ship_today ?? 0
        } else if (src === 'grandchild') {
            const portraitLoc = portraitData.grand_children_locations?.find(
                (loc) => loc.location_id === locationId,
            )
            return portraitLoc?.open_units_required_ship_today ?? 0
        }
        return 0
    }

    const makeRow = (
        loc: Location,
        src: 'current' | 'parent' | 'child' | 'grandchild',
    ) => {
        const locationDescription = loc.location_description ?? ''
        const isStoreLocation = isStore(locationDescription)
        const isPriority =
            isStoreLocation && isPriorityStore(locationDescription)

        const firstCell: Cell = {
            value: locationDescription,
            childOf: src,
        }

        if (isPriority) {
            firstCell.icon = React.createElement(Icon, {
                svg: ShipBoxIcon,
                size: 'sm',
                'aria-hidden': true,
            })
        }

        return [
            firstCell,
            {
                value: formatWithCommas(loc.open_late_units ?? 0),
                raw: String(loc.open_late_units ?? 0),
                className: (loc.open_late_units ?? 0) > 0 ? 'hc-clr-error' : '',
            },
            {
                value: formatWithCommas(loc.carry_over_units ?? 0),
                raw: String(loc.carry_over_units ?? 0),
            },
            {
                value: formatWithCommas(
                    getPortraitValue(loc.location_id ?? 0, src),
                ),
                raw: String(getPortraitValue(loc.location_id ?? 0, src)),
            },
            {
                value: formatWithCommas(
                    loc.open_by_store_close_to_be_packed ?? 0,
                ),
                raw: String(loc.open_by_store_close_to_be_packed ?? 0),
            },
            {
                value: formatWithCommas(loc.sorted_units ?? 0),
                raw: String(loc.sorted_units ?? 0),
            },
            {
                value: formatWithCommas(loc.forecast_units ?? 0),
                raw: String(loc.forecast_units ?? 0),
            },
            {
                ...applyConditionalFormatting(
                    loc.inf_percent ?? '0%',
                    loc.inf_percent_color,
                ),
                raw: (loc.inf_percent ?? '0%').replace('%', ''), // Store percentage as raw number for sorting
            },
        ]
    }

    table.rows = landscapeData ? gatherLocationRows(landscapeData, makeRow) : []
    table.rows = pinAndSortRows(table.rows, (a, b) => {
        const num = (cell: Cell) =>
            parseInt(cell.value.match(/\d+/)?.[0] ?? '0', 10)
        return num(a[0]) - num(b[0])
    })

    const tableWithLinks = addHyperlinkToFirstColumn(table, currentLocation)

    return buildPayloadFromTable(tableWithLinks, currentLocation, t, toggles)
}
